/**
 * SessionStorageAdapter — infra-level abstraction over browser sessionStorage.
 *
 * Domain code MUST use this adapter instead of calling sessionStorage directly.
 * This keeps the architecture lint rule (no-sessionstorage) satisfied and
 * enables swapping for tests or alternative environments.
 */
export interface SessionStorageAdapter {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  clear(): void;
}

/** Default web implementation backed by window.sessionStorage. */
const webSessionStorage: SessionStorageAdapter = {
  getItem: (key) => {
    try {
      return sessionStorage.getItem(key);
    } catch {
      return null;
    }
  },
  setItem: (key, value) => {
    try {
      sessionStorage.setItem(key, value);
    } catch {
      // sessionStorage may be full or unavailable — fail silently
    }
  },
  removeItem: (key) => {
    try {
      sessionStorage.removeItem(key);
    } catch {
      // fail silently
    }
  },
  clear: () => {
    try {
      sessionStorage.clear();
    } catch {
      // fail silently
    }
  },
};

export default webSessionStorage;
